// BRIEF 137 §4(a) — THE ICS COMPOSER, ONCE, FOR BOTH PLANES.
//
// §4 makes the SERVER attach an .ics to the Outlook draft it composes, and its
// own fence says how that must not be done: *"Not a second .ics composer. If
// you find yourself writing VEVENT lines on the server, (a) was not done."*
// So the composer leaves `App.tsx` — where it was three consts inside the App
// component, reachable only by a prop drilled through Dashboard, CaseView and
// AgendaView — and lands here, where `server/src/routes/agenda.js` can
// `require` it.
//
// **EXTRACTION ONLY (rule 3: moving and bettering are two commits).** Not one
// byte of the arithmetic changes. `mkIcsDate`'s ISO round-trip, the one-hour
// default end, the `HH:MM` re-derivation from a `Date`, the field order, the
// `type ? type + ": " : ""` subject prefix, the em-dash before the case ref,
// the `lexcrm-<id>@lexcrm` UID and the CRLF joins are all carried across as
// they stood at `App.tsx:10335-10365@1305d044`. What is WRONG with them stays
// wrong on purpose — see THE KNOWN DEFECTS below, which are recorded rather
// than repaired so that a later brief can rule on them with the behaviour in
// front of it.
//
// `downloadIcs` DOES NOT MOVE. §4(a) names it as the browser-only half: it
// builds a `Blob`, mints an object URL and clicks an anchor, none of which the
// server has. It stays in `App.tsx` and now calls `icsCalendar` for the
// wrapper it used to concatenate inline.
//
// ── WHY AN `.mjs` UNDER `src/lib/` ─────────────────────────────────────────
// The server is CommonJS and cannot `require` a `.ts`. This tree already
// carries exactly this shape for exactly this reason — one pure ESM module
// imported by the client and `require`d by the server:
//
//   src/lib/cddIdentity.mjs      server/src/routes/cdd.js:42
//   src/lib/mentionTokens.mjs    server/src/routes/chatMessages.js:17
//   src/lib/caseRefPattern.mjs   server/src/lib/caseRefSettings.js:33
//   src/lib/themeVars.mjs        server/src/routes/themes.js (135 §1b)
//
// ── THE KNOWN DEFECTS, CARRIED ─────────────────────────────────────────────
// Named here because a reader of a fresh file has no way to tell a deliberate
// carry from an oversight, and because §4(a) forbids fixing them in this
// commit:
//
//   · NO ESCAPING. RFC 5545 §3.3.11 escapes `\`, `;`, `,` and newlines inside
//     a TEXT value; nothing here does. A location containing a comma splits
//     the property. Live since the composer was written.
//   · NO LINE FOLDING. RFC 5545 §3.1 folds at 75 octets; a long DESCRIPTION
//     goes out as one long line.
//   · THE ONE-HOUR END IS COMPUTED FROM `endD.getHours()` AND DISCARDS THE
//     DATE, so an event starting at 23:30 ends at 00:30 ON THE SAME DAY —
//     i.e. before it starts. `ev.date` is re-used verbatim for both.
//
// None of the three is reachable differently from the server than from the
// browser: it is the same function, so the draft's attachment and the
// downloaded file are the same bytes for the same event. A FOURTH could not be
// carried, and the block below is why.
//
// ── THE THIRD DEFECT WAS NOT CARRYABLE, AND THIS IS THE "BETTER" COMMIT ────
//
// `mkIcsDate` read its wall-clock string IN THE RUNTIME'S OWN ZONE — a bare
// `new Date("2026-08-26T09:00:00")` is parsed as local time, and
// `.toISOString()` then converts. That was invisible while the only caller was
// a browser: a Brussels lawyer's browser IS Europe/Brussels, so 09:00 went out
// as 07:00Z and Outlook showed 09:00.
//
// **MEASURED, and it is why this could not simply be carried: this box's Node
// runs in UTC** (`Intl.DateTimeFormat().resolvedOptions().timeZone` = "UTC").
// So the very first thing §4(c) does with the extracted composer — attach an
// .ics to a draft the SERVER builds — would emit `20260826T090000Z` for that
// same event, and every invitation LexCRM sends would name a time one or two
// hours late. The move is what makes the latent defect reachable, so it
// belongs to this brief's lane rather than to a register row.
//
// THE ZONE IS PINNED, NOT INFERRED. `AgendaEvent.date`/`time` hold Brussels
// wall-clock — that is not an assumption here, it is what `m365.js` asserts in
// five places by handing Graph `timeZone: 'Europe/Brussels'` beside the very
// same strings (`:1471-1483`, `:1676-1677`). Pinning therefore gives the
// browser back exactly the answer it already produced for a Brussels user and
// gives every other runtime — this server, a lawyer travelling with a laptop —
// the SAME answer instead of its own.
//
// THE OFFSET IS COMPUTED AT THE MOMENT IN QUESTION, so March and October
// resolve correctly rather than through a fixed +1. It carries one limit: the
// offset is sampled at the wall clock read as UTC, which is within two hours
// of the true instant, so a start inside the switchover hour itself can land
// on the wrong side of it. Two hours a year, in the hour nobody schedules a
// hearing in, against every day of the year being wrong on the server.
//
// **`formatToParts`, NOT `Date.parse(toLocaleString(…))`** — and that is a
// measurement, not a preference. This tree's existing spelling of this idiom
// (`ThemeManager.tsx:60-73`) compares two `toLocaleString` renderings with
// `hour12: false`, and en-US renders MIDNIGHT AS "24:00:00" under that flag:
//
//     new Date("2026-12-31T23:30:00Z").toLocaleString("en-US",
//       { hour12: false, timeZone: "Europe/Brussels" })   ->  "1/1/2027, 24:30:00"
//
// `Date.parse` refuses hour 24, the offset comes back NaN, and the instant is
// an Invalid Date. `hourCycle: "h23"` renders the same moment "00:30:00", and
// reading the fields directly never goes through a locale string at all.
// (The `ThemeManager` copy is a LIVE defect — see the note at the foot of this
// file. It is not repaired here: that is another screen, with its own glance.)

const BXL = "Europe/Brussels";

const BXL_PARTS = new Intl.DateTimeFormat("en-US", {
  timeZone: BXL,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

/** Brussels' offset from UTC, in ms, AT the given instant. */
function bxlOffsetMs(instant) {
  const p = {};
  for (const { type, value } of BXL_PARTS.formatToParts(instant)) p[type] = value;
  return (
    Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) -
    instant.getTime()
  );
}

/* THE SHAPE IS MATCHED, NOT ATTEMPTED. `new Date(x + "Z")` is not a test: V8
   parses `String(someDate) + "Z"` happily by ignoring the trailing garbage, so
   a caller handing this an instant would have had the Brussels offset
   subtracted from a time that already carried one. The wall-clock reading
   applies to a wall-clock spelling and to nothing else; `evToVevent` builds
   exactly this shape and is the only caller in the tree. */
const WALL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

/** The instant a `YYYY-MM-DDTHH:MM:SS` wall clock names in Brussels. */
function brusselsInstant(local) {
  if (typeof local !== "string" || !WALL.test(local)) return null;
  const naive = new Date(local + "Z");
  if (isNaN(naive.getTime())) return null;
  return new Date(naive.getTime() - bxlOffsetMs(naive));
}

/**
 * `YYYYMMDDTHHMMSSZ` for a Brussels wall clock, in every runtime.
 * Anything that is not a wall-clock spelling — a Date, an instant with its own
 * offset — falls through to the original parse, unchanged. Empty for falsy,
 * as before.
 */
export function mkIcsDate(dt) {
  if (!dt) return "";
  const d = brusselsInstant(dt) || new Date(dt);
  return d.toISOString().replace(/[-:]/g, "").replace(/\.\d+/, "");
}

/**
 * One VEVENT block, CRLF-joined, WITHOUT the VCALENDAR wrapper.
 *
 * @param ev        the AgendaEvent as either plane holds it — `date`, `time`,
 *                  `type`, `title`, `location`, `notes`, `id` are read.
 * @param caseObj   the matter, for the ` — <ref>` tail on DESCRIPTION. May be
 *                  null (the agenda's own download passes null).
 * @param organizer one address, or falsy for no ORGANIZER line.
 * @param collabs   addresses, one ATTENDEE line each. May be empty.
 */
export function evToVevent(ev, caseObj, organizer, collabs) {
  const start = ev.date + (ev.time ? "T" + ev.time + ":00" : "T09:00:00");
  const endD = new Date(start);
  endD.setHours(endD.getHours() + 1);
  const end =
    ev.date +
    (ev.time
      ? "T" +
        String(endD.getHours()).padStart(2, "0") +
        ":" +
        String(endD.getMinutes()).padStart(2, "0") +
        ":00"
      : "T10:00:00");
  const lines = [
    "BEGIN:VEVENT",
    `DTSTART:${mkIcsDate(start)}`,
    `DTEND:${mkIcsDate(end)}`,
    `SUMMARY:${(ev.type ? ev.type + ": " : "") + ev.title}`,
    `LOCATION:${ev.location || ""}`,
    `DESCRIPTION:${(ev.notes || "") + (caseObj ? " — " + caseObj.ref : "")}`,
    `UID:lexcrm-${ev.id}@lexcrm`,
  ];
  if (organizer) lines.push(`ORGANIZER:mailto:${organizer}`);
  (collabs || []).forEach((e) => lines.push(`ATTENDEE:mailto:${e}`));
  lines.push("END:VEVENT");
  return lines.join("\r\n");
}

/**
 * The VCALENDAR wrapper `downloadIcs` used to concatenate inline
 * (`App.tsx:10366-10372@1305d044`) — the same three header lines and the same
 * trailer, byte for byte, so a file downloaded from the browser and a file
 * attached by the server are identical.
 */
export function icsCalendar(vevent) {
  return (
    "BEGIN:VCALENDAR\r\nVERSION:2.0\r\nPRODID:-//LexCRM//EN\r\n" +
    vevent +
    "\r\nEND:VCALENDAR"
  );
}

// ── SURFACED WHILE WRITING THIS FILE — AND PINNED BY 169 §0c ────────────────
//
// `ThemeManager.tsx:61-73` `endOfDayBrusselsISO` carried the `hour12: false`
// spelling described above, and on an engine that resolves it to h24 it THREW
// for every winter date (measured on this box's Node v20.20.2 / ICU 78.2):
//
//   endOfDayBrusselsISO("2026-08-26") -> "2026-08-26T21:59:59.000Z"   (CEST)
//   endOfDayBrusselsISO("2026-12-31") -> RangeError: Invalid time value
//   endOfDayBrusselsISO("2026-01-15") -> RangeError: Invalid time value
//
// 23:59:59Z is 00:59:59 the next day in CET, en-US rendered that "24:59:59",
// `Date.parse` returned NaN, and `new Date(NaN).toISOString()` threw. The one
// call site — `savePush` at `:1052` — evaluates it BEFORE its own `try`, so
// the throw escaped an async click handler: between late October and late
// March, "push a theme until <date>" saved nothing and showed no error.
//
// Brief 169 §0c replaced `hour12: false` with `hourCycle: "h23"` at both
// sites — the explicit cycle the code meant, byte-identical output wherever
// the engine already printed h23 (HeadlessChrome 151 on this box does).
